import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Search, 
  Users, 
  HeartHandshake,
  Save,
  TrendingUp,
  Target,
  Gift,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { currencySymbols } from '@/types';
import { format } from 'date-fns';

interface DonationRecord {
  id: string;
  member_id?: string;
  member_name?: string;
  donor_name?: string;
  amount: number;
  is_in_kind: boolean;
  in_kind_description?: string;
  donation_date: string;
  is_pledge: boolean;
  pledge_start_date?: string | null;
  pledge_due_date?: string | null;
  pledge_redeemed_date?: string | null;
}

interface MemberForDonation {
  id: string;
  full_name: string;
  title?: string;
  is_selected: boolean;
  amount: string;
  is_in_kind: boolean;
  in_kind_description: string;
  is_pledge: boolean;
  pledge_start_date: string;
  pledge_due_date: string;
}

export default function DonationEventDetail() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentOrganization, addDonation } = useAppStore();
  
  const [event, setEvent] = useState<{ id: string; name: string; description?: string; target_amount?: number; start_date?: string; end_date?: string; is_active: boolean; total_raised?: number; donor_count?: number } | null>(null);
  const [members, setMembers] = useState<MemberForDonation[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<MemberForDonation[]>([]);
  const [donations, setDonations] = useState<DonationRecord[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('collect');
  const [redeemTarget, setRedeemTarget] = useState<DonationRecord | null>(null);
  
  // External donor
  const [externalDonorName, setExternalDonorName] = useState('');
  const [externalAmount, setExternalAmount] = useState('');
  const [externalIsInKind, setExternalIsInKind] = useState(false);
  const [externalInKindDesc, setExternalInKindDesc] = useState('');
  const [externalIsPledge, setExternalIsPledge] = useState(false);
  const [externalPledgeStartDate, setExternalPledgeStartDate] = useState('');
  const [externalPledgeDueDate, setExternalPledgeDueDate] = useState('');

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';
  const isExpired = event ? (!event.is_active || (event.end_date ? new Date(event.end_date) < new Date() : false)) : false;

  useEffect(() => {
    if (eventId) {
      fetchEventData();
    }
  }, [eventId, currentOrganization]);

  useEffect(() => {
    const filtered = members.filter((m) =>
      m.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredMembers(filtered);
  }, [searchQuery, members]);

  const fetchEventData = async () => {
    if (!eventId || !currentOrganization) return;

    setIsLoading(true);
    try {
      // Fetch event details
      const { data: eventData, error: eventError } = await supabase
        .from('donation_events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (eventError) throw eventError;
      setEvent(eventData);

      // Fetch members
      const { data: membersData } = await supabase
        .from('members')
        .select('id, full_name, title')
        .eq('organization_id', currentOrganization.id)
        .eq('status', 'active')
        .order('full_name');

      const formattedMembers = (membersData || []).map((m) => ({
        ...m,
        is_selected: false,
        amount: '',
        is_in_kind: false,
        in_kind_description: '',
        is_pledge: false,
        pledge_start_date: '',
        pledge_due_date: '',
      }));

      setMembers(formattedMembers);
      setFilteredMembers(formattedMembers);

      // Fetch existing donations
      const { data: donationsData } = await supabase
        .from('donations')
        .select(`
          id,
          member_id,
          donor_name,
          amount,
          is_in_kind,
          in_kind_description,
          donation_date,
          is_pledge,
          pledge_start_date,
          pledge_due_date,
          member:members(full_name)
        `)
        .eq('event_id', eventId)
        .order('donation_date', { ascending: false });

      const formattedDonations = (donationsData || []).map((d) => ({
        id: d.id,
        member_id: d.member_id,
        member_name: (d.member as { full_name?: string } | null)?.full_name,
        donor_name: d.donor_name,
        amount: d.amount,
        is_in_kind: d.is_in_kind,
        in_kind_description: d.in_kind_description,
        donation_date: d.donation_date,
        is_pledge: d.is_pledge,
        pledge_start_date: d.pledge_start_date,
        pledge_due_date: d.pledge_due_date,
      }));

      setDonations(formattedDonations);
    } catch (error) {
      console.error('Error fetching event data:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMember = (index: number) => {
    const newMembers = [...members];
    const memberIndex = members.findIndex(
      (m) => m.id === filteredMembers[index].id
    );
    if (memberIndex !== -1) {
      newMembers[memberIndex].is_selected = !newMembers[memberIndex].is_selected;
      setMembers(newMembers);
    }
  };

  const updateMemberField = (index: number, field: keyof MemberForDonation, value: MemberForDonation[typeof field]) => {
    const newMembers = [...members];
    const memberIndex = members.findIndex(
      (m) => m.id === filteredMembers[index].id
    );
    if (memberIndex !== -1) {
      newMembers[memberIndex] = { ...newMembers[memberIndex], [field]: value };
      setMembers(newMembers);
    }
  };

  const handleSaveDonations = async () => {
    if (isExpired) {
      toast.error('This donation event has ended. New donations can no longer be recorded.');
      return;
    }

    const selectedMembers = members.filter((m) => m.is_selected && (parseFloat(m.amount) > 0 || m.is_in_kind));
    
    if (selectedMembers.length === 0 && !externalDonorName && !externalAmount) {
      toast.error('Please select at least one donor and enter an amount');
      return;
    }

    if (!eventId) return;

    setIsSubmitting(true);

    try {
      // Save member donations
      for (const member of selectedMembers) {
        const { data, error } = await supabase
          .from('donations')
          .insert({
            event_id: eventId,
            member_id: member.id,
            amount: parseFloat(member.amount) || 0,
            is_in_kind: member.is_in_kind,
            in_kind_description: member.is_in_kind ? member.in_kind_description : null,
            donation_date: new Date().toISOString().split('T')[0],
            is_pledge: member.is_pledge,
            pledge_start_date: member.is_pledge ? member.pledge_start_date || null : null,
            pledge_due_date: member.is_pledge ? member.pledge_due_date || null : null,
          })
          .select()
          .single();

        if (error) throw error;
        addDonation(data);
      }

      // Save external donor if provided
      if (externalDonorName && (parseFloat(externalAmount) > 0 || externalIsInKind)) {
        const { data, error } = await supabase
          .from('donations')
          .insert({
            event_id: eventId,
            donor_name: externalDonorName,
            amount: parseFloat(externalAmount) || 0,
            is_in_kind: externalIsInKind,
            in_kind_description: externalIsInKind ? externalInKindDesc : null,
            donation_date: new Date().toISOString().split('T')[0],
            is_pledge: externalIsPledge,
            pledge_start_date: externalIsPledge ? externalPledgeStartDate || null : null,
            pledge_due_date: externalIsPledge ? externalPledgeDueDate || null : null,
          })
          .select()
          .single();

        if (error) throw error;
        addDonation(data);
      }

      toast.success('Donations recorded successfully');
      
      // Reset form
      setMembers(members.map(m => ({ ...m, is_selected: false, amount: '', is_in_kind: false, in_kind_description: '', is_pledge: false, pledge_start_date: '', pledge_due_date: '' })));
      setExternalDonorName('');
      setExternalAmount('');
      setExternalIsInKind(false);
      setExternalInKindDesc('');
      setExternalIsPledge(false);
      setExternalPledgeStartDate('');
      setExternalPledgeDueDate('');
      
      // Refresh data
      fetchEventData();
      setActiveTab('history');
    } catch (error) {
      console.error('Error saving donations:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalRaised = donations.reduce((sum, d) => sum + (d.amount || 0), 0);
  const progress = event?.target_amount ? Math.min(100, Math.round((totalRaised / event.target_amount) * 100)) : 0;

  const selectedCount = members.filter((m) => m.is_selected).length;
  const selectedTotal = members
    .filter((m) => m.is_selected)
    .reduce((sum, m) => sum + (parseFloat(m.amount) || 0), 0);

  const handleRedeemPledge = async (donation: DonationRecord) => {
    const today = new Date().toISOString().split('T')[0];

    try {
      const { error } = await supabase
        .from('donations')
        .update({
          is_pledge: false,
          pledge_redeemed_date: today,
        })
        .eq('id', donation.id);

      if (error) {
        const missingRedeemedColumn =
          error.code === '42703' ||
          error.message.toLowerCase().includes('pledge_redeemed_date');

        if (missingRedeemedColumn) {
          const { error: fallbackError } = await supabase
            .from('donations')
            .update({ is_pledge: false })
            .eq('id', donation.id);

          if (fallbackError) throw fallbackError;
        } else {
          throw error;
        }
      }

      toast.success('Pledge redeemed successfully');
      setRedeemTarget(null);
      fetchEventData();
    } catch (error) {
      console.error('Error redeeming pledge:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Event not found</p>
        <Button onClick={() => navigate('/donations')} className="mt-4">
          Back to Donations
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Button variant="ghost" onClick={() => navigate('/donations')} className="mb-2">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">{event.name}</h1>
          {event.description && (
            <p className="text-muted-foreground">{event.description}</p>
          )}
        </div>
      </div>

      {/* Progress Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <span className="text-2xl font-bold">{currencySymbol}{totalRaised.toLocaleString()}</span>
              <span className="text-muted-foreground">raised</span>
            </div>
            {event.target_amount && (
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <span className="font-medium">of {currencySymbol}{event.target_amount.toLocaleString()}</span>
              </div>
            )}
          </div>
          {event.target_amount && (
            <>
              <Progress value={progress} className="h-3" />
              <p className="text-sm text-muted-foreground mt-2">{progress}% of target reached</p>
            </>
          )}
        </CardContent>
      </Card>

      {isExpired && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            This donation event has ended on{' '}
            <strong>{event.end_date ? format(new Date(event.end_date), 'MMM dd, yyyy') : 'the set date'}</strong>.
            {' '}New donations can no longer be recorded.
          </span>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="collect" disabled={isExpired}>Collect Donation</TabsTrigger>
          <TabsTrigger value="history">Donation History ({donations.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="collect" className="space-y-4">
          {/* External Donor */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Gift className="w-5 h-5" />
                External Donor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Donor Name</Label>
                  <Input
                    placeholder="Enter donor name"
                    value={externalDonorName}
                    onChange={(e) => setExternalDonorName(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={externalAmount}
                    onChange={(e) => setExternalAmount(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={externalIsInKind}
                    onCheckedChange={setExternalIsInKind}
                  />
                  <Label>In-Kind Contribution</Label>
                </div>
                {externalIsInKind && (
                  <Input
                    placeholder="Description of contribution"
                    value={externalInKindDesc}
                    onChange={(e) => setExternalInKindDesc(e.target.value)}
                    className="flex-1"
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={externalIsPledge}
                  onCheckedChange={setExternalIsPledge}
                />
                <Label>Pledge</Label>
              </div>
              {externalIsPledge && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-sm">Pledge Start Date</Label>
                    <Input
                      type="date"
                      value={externalPledgeStartDate}
                      onChange={(e) => setExternalPledgeStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Pledge Due Date</Label>
                    <Input
                      type="date"
                      value={externalPledgeDueDate}
                      onChange={(e) => setExternalPledgeDueDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Members List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Member Donations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Selected Summary */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{selectedCount} selected</span>
                <span className="font-medium">Total: {currencySymbol}{selectedTotal.toLocaleString()}</span>
              </div>

              {/* Members */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredMembers.map((member, index) => (
                  <div 
                    key={member.id}
                    className={`p-3 rounded-lg border transition-colors ${
                      member.is_selected ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={member.is_selected}
                        onCheckedChange={() => toggleMember(index)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{member.full_name}</p>
                        <p className="text-sm text-muted-foreground">{member.title}</p>
                      </div>
                      <div className="w-28">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={member.amount}
                          onChange={(e) => updateMemberField(index, 'amount', e.target.value)}
                          disabled={!member.is_selected}
                          className="text-right"
                        />
                      </div>
                    </div>
                    {member.is_selected && (
                      <div className="mt-3 pt-3 border-t flex flex-col gap-3">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={member.is_in_kind}
                              onCheckedChange={(v) => updateMemberField(index, 'is_in_kind', v)}
                            />
                            <Label className="text-sm">In-Kind</Label>
                          </div>
                          {member.is_in_kind && (
                            <Input
                              placeholder="Description"
                              value={member.in_kind_description}
                              onChange={(e) => updateMemberField(index, 'in_kind_description', e.target.value)}
                              className="flex-1"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={member.is_pledge}
                            onCheckedChange={(v) => updateMemberField(index, 'is_pledge', v)}
                          />
                          <Label className="text-sm">Pledge</Label>
                        </div>
                        {member.is_pledge && (
                          <div className="grid grid-cols-2 gap-3 mt-2">
                            <div>
                              <Label className="text-sm">Pledge Start Date</Label>
                              <Input
                                type="date"
                                value={member.pledge_start_date}
                                onChange={(e) => updateMemberField(index, 'pledge_start_date', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Pledge Due Date</Label>
                              <Input
                                type="date"
                                value={member.pledge_due_date}
                                onChange={(e) => updateMemberField(index, 'pledge_due_date', e.target.value)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <Button 
            className="w-full"
            onClick={handleSaveDonations}
            disabled={isSubmitting || isExpired}
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Donations
          </Button>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Donation History</CardTitle>
            </CardHeader>
            <CardContent>
              {donations.length > 0 ? (
                <div className="space-y-3">
                  {donations.map((donation) => (
                    <div 
                      key={donation.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      <div>
                        <p className="font-medium">
                          {donation.member_name || donation.donor_name || 'Anonymous'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(donation.donation_date), 'MMM dd, yyyy')}
                        </p>
                        {donation.is_in_kind && donation.in_kind_description && (
                          <p className="text-sm text-muted-foreground">
                            In-kind: {donation.in_kind_description}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">
                          {currencySymbol}{donation.amount.toLocaleString()}
                        </p>
                        {donation.is_in_kind && (
                          <Badge variant="outline" className="text-xs">In-Kind</Badge>
                        )}
                        {donation.is_pledge && (
                          <Badge variant="secondary" className="text-xs ml-1">Pledge</Badge>
                        )}
                        {donation.is_pledge && donation.pledge_due_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Due: {format(new Date(donation.pledge_due_date), 'MMM dd, yyyy')}
                          </p>
                        )}
                        {donation.is_pledge && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => setRedeemTarget(donation)}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Redeem Pledge
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <HeartHandshake className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No donations recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!redeemTarget} onOpenChange={(open) => !open && setRedeemTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Redeem pledge?</AlertDialogTitle>
            <AlertDialogDescription>
              Record pledge redemption for {currencySymbol}{(redeemTarget?.amount || 0).toLocaleString()}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => redeemTarget && handleRedeemPledge(redeemTarget)}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
